import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Gauge, X } from "lucide-react";
import { fetchBelief, castBelief, clearBelief } from "../lib/api";
import { getVoterId } from "../lib/voter";

/**
 * BeliefSlider — "How much of this story do you believe?"
 * 0 = pure disinfo / hoax · 50 = undecided · 100 = absolute truth.
 * Anonymous one-vote-per-browser (localStorage voter id). Re-submit replaces; clear removes.
 * After voting, the user sees their score next to the community average + a 5-bucket histogram.
 */

// Score → human label. Tuned for a deep-archive vibe (not vanilla "agree/disagree").
const SCORE_LABELS = [
  { max: 9,   label: "Pure Disinfo",       color: "#ff3b30" },
  { max: 19,  label: "Hoax Territory",     color: "#ff5a47" },
  { max: 29,  label: "Heavily Skeptical",  color: "#ff8c5a" },
  { max: 44,  label: "Doubtful",           color: "#ffaa00" },
  { max: 55,  label: "Undecided",          color: "#ffd60a" },
  { max: 70,  label: "Plausible",          color: "#a3e635" },
  { max: 85,  label: "Strong Belief",      color: "#00ff88" },
  { max: 95,  label: "Near Certain",       color: "#34d399" },
  { max: 100, label: "Absolute Truth",     color: "#a78bfa" },
];

function labelFor(score) {
  for (const row of SCORE_LABELS) {
    if (score <= row.max) return row;
  }
  return SCORE_LABELS[SCORE_LABELS.length - 1];
}

export default function BeliefSlider({ targetType, targetId }) {
  const [stats, setStats] = useState(null); // { count, avg, distribution, buckets, my_score }
  const [score, setScore] = useState(50);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const voterId = useMemo(() => getVoterId(), []);

  // Has the user submitted yet? Drives reveal of avg + histogram. Mirrors stats.my_score on load.
  const hasVoted = stats?.my_score != null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBelief(targetType, targetId, voterId)
      .then((s) => {
        if (cancelled) return;
        setStats(s);
        if (s?.my_score != null) setScore(s.my_score);
      })
      .catch(() => !cancelled && setStats({ count: 0, avg: null, distribution: [0,0,0,0,0], buckets: [], my_score: null }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [targetType, targetId, voterId]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const s = await castBelief(targetType, targetId, voterId, score);
      setStats(s);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = async () => {
    if (submitting || !hasVoted) return;
    setSubmitting(true);
    try {
      const s = await clearBelief(targetType, targetId, voterId);
      setStats(s);
      setScore(50);
    } finally {
      setSubmitting(false);
    }
  };

  const my = labelFor(score);
  const avgLabel = stats?.avg != null ? labelFor(Math.round(stats.avg)) : null;
  const maxBucket = stats?.distribution ? Math.max(...stats.distribution, 1) : 1;

  // The fill % for the slider track — same as score, drives the colored progress bar.
  const fillPct = `${score}%`;

  if (loading) {
    return (
      <section
        data-testid="belief-slider"
        className="mt-10 border border-white/10 bg-[#0a0a0a] p-6 flex items-center gap-3 font-mono text-[10px] tracking-[0.25em] text-zinc-500"
      >
        <Loader2 size={12} className="animate-spin" /> CALIBRATING BELIEF INDEX…
      </section>
    );
  }

  return (
    <section
      data-testid="belief-slider"
      className="mt-10 border border-white/10 bg-[#0a0a0a] p-6 print-hide-children"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2">
          <Gauge size={11} /> BELIEF INDEX // HOW MUCH OF THIS STORY DO YOU BELIEVE?
        </div>
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">
          {stats?.count || 0} VOTE{stats?.count === 1 ? "" : "S"}
        </div>
      </div>

      {/* Big label above the slider — color + words shift with the score */}
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <span
            data-testid="belief-score-label"
            className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl"
            style={{ color: my.color }}
          >
            {my.label}
          </span>
          <span className="font-mono text-sm text-zinc-400" data-testid="belief-score-value">
            {score}<span className="text-zinc-600">/100</span>
          </span>
        </div>
        {hasVoted && (
          <button
            type="button"
            onClick={reset}
            data-testid="belief-clear-btn"
            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#ff3b30] no-print"
            title="Clear my vote"
          >
            <X size={11} /> Clear vote
          </button>
        )}
      </div>

      {/* Slider with a gradient track that scales with score */}
      <div className="relative w-full no-print">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 bg-white/5 border border-white/10 pointer-events-none" />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-2 pointer-events-none"
          style={{
            width: fillPct,
            background: `linear-gradient(90deg, #ff3b30 0%, #ffd60a 50%, #00ff88 100%), #0a0a0a`,
            backgroundSize: `${100 * (100 / Math.max(score, 1))}% 100%`,
          }}
        />
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          data-testid="belief-slider-input"
          className="relative w-full h-8 appearance-none bg-transparent cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-7
                     [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-grab
                     [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-[#050505]
                     [&::-webkit-slider-thumb]:rounded-none
                     [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-7
                     [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-grab
                     [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-[#050505]
                     [&::-moz-range-thumb]:rounded-none"
        />
      </div>

      {/* End-stop labels */}
      <div className="flex justify-between font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-1">
        <span>0 // PURE DISINFO</span>
        <span className="hidden sm:inline">50 // UNDECIDED</span>
        <span>100 // ABSOLUTE TRUTH</span>
      </div>

      {/* Submit row */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="belief-submit-btn"
          className="inline-flex items-center gap-2 bg-[#a78bfa] text-[#050505] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] no-print"
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
          {hasVoted ? "Update Vote" : "Submit Belief"}
        </button>
        {hasVoted && (
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            YOUR VOTE LOCKED — DRAG SLIDER + RESUBMIT TO CHANGE
          </span>
        )}
      </div>

      {/* AFTER-VOTE REVEAL — yours vs. average + distribution histogram */}
      {hasVoted && stats?.avg != null && (
        <div
          data-testid="belief-results"
          className="mt-6 border-t border-white/10 pt-5 grid sm:grid-cols-2 gap-6"
        >
          <div className="space-y-2">
            <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">YOU VS. THE ARCHIVE</div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 w-20">YOU</span>
              <span className="font-heading font-black text-2xl" style={{ color: my.color }}>
                {stats.my_score}
              </span>
              <span className="font-mono text-xs text-zinc-400">{my.label}</span>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 w-20">AVERAGE</span>
              <span
                className="font-heading font-black text-2xl"
                style={{ color: avgLabel.color }}
                data-testid="belief-avg"
              >
                {stats.avg}
              </span>
              <span className="font-mono text-xs text-zinc-400">{avgLabel.label}</span>
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-600 pt-1">
              {stats.my_score > stats.avg
                ? `YOU BELIEVE THIS ${Math.round(stats.my_score - stats.avg)} POINTS MORE THAN AVG`
                : stats.my_score < stats.avg
                ? `YOU BELIEVE THIS ${Math.round(stats.avg - stats.my_score)} POINTS LESS THAN AVG`
                : `// YOU MATCH THE AVERAGE EXACTLY`}
            </div>
          </div>

          {/* Distribution histogram */}
          <div className="space-y-1.5">
            <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">DISTRIBUTION</div>
            {stats.distribution.map((n, idx) => {
              const bucket = stats.buckets[idx];
              const widthPct = (n / maxBucket) * 100;
              // Bucket color matches the midpoint of the range
              const mid = idx === 0 ? 10 : idx === 1 ? 30 : idx === 2 ? 50 : idx === 3 ? 70 : 90;
              const c = labelFor(mid).color;
              return (
                <div key={idx} className="flex items-center gap-2 font-mono text-[10px]">
                  <span className="text-zinc-500 w-14 tracking-[0.2em]">{bucket}</span>
                  <div className="flex-1 h-3 bg-white/5 border border-white/10">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${widthPct}%`, background: c }}
                    />
                  </div>
                  <span className="text-zinc-400 w-8 text-right">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
